import React from 'react';
import PropTypes from 'prop-types';
import { mcHouseFor } from '../utils/angularAspects';

/**
 * North Indian Natal Chart — 5×5 grid, 4 empty corners, center 3×3 for controls.
 *
 * House layout (counter-clockwise from top-center = House 1):
 *
 *  ┌──────┬──────┬──────┬──────┬──────┐
 *  │empty │  2   │  1   │  12  │empty │
 *  ├──────┼──────┴──────┴──────┼──────┤
 *  │  3   │                    │  11  │
 *  ├──────┤      CENTER        ├──────┤
 *  │  4   │     (inputs)       │  10  │
 *  ├──────┤                    ├──────┤
 *  │  5   │                    │  9   │
 *  ├──────┼──────┬──────┬──────┼──────┤
 *  │empty │  6   │  7   │  8   │empty │
 *  └──────┴──────┴──────┴──────┴──────┘
 */

// Special aspect planet colors (only for non-7th aspects)
const SPECIAL_ASPECT_COLORS = {
  Ma: '#d32f2f',   // Red
  Ju: '#2e7d32',   // Green
  Sa: '#000000',   // Black
  Ra: '#1565c0',   // Blue
  Ke: '#f9a825',   // Yellow
};

// 7th aspect style (universal, all planets)
const SEVENTH_ASPECT_COLOR = '#d500f9'; // Magenta
const SEVENTH_ASPECT_DASH  = '4,4';     // Dotted

// Vedic special aspects: planet abbr → array of house offsets (counting from the planet's own house)
const VEDIC_SPECIAL_ASPECTS = {
  Ma: [4, 8],
  Ju: [5, 9],
  Sa: [3, 10],
  Ra: [5, 9],
  Ke: [5, 9],
};

// Line thickness for special aspect planets
const SPECIAL_ASPECT_WIDTH = {
  Ma: 2.5,
  Ju: 2.0,
  Sa: 2.0,
  Ra: 1.5,
  Ke: 1.5,
};

// Grid dimensions
const COLS = 5;
const ROWS = 5;

// Cell size — large enough for full dialog width
const CW = 253; // cell width
const CH = 163; // cell height
const SVG_W = COLS * CW;
const SVG_H = ROWS * CH;

// House-to-grid-cell mapping
const HOUSE_CELLS = {
  1:  { row: 0, col: 2 },
  2:  { row: 0, col: 1 },
  3:  { row: 1, col: 0 },
  4:  { row: 2, col: 0 },
  5:  { row: 3, col: 0 },
  6:  { row: 4, col: 1 },
  7:  { row: 4, col: 2 },
  8:  { row: 4, col: 3 },
  9:  { row: 3, col: 4 },
  10: { row: 2, col: 4 },
  11: { row: 1, col: 4 },
  12: { row: 0, col: 3 },
};

// Compute the inner-edge anchor point for a house cell.
// "Inner edge" = the edge facing the center 3×3 area.
const houseInnerEdgeMid = (houseNum) => {
  const cell = HOUSE_CELLS[houseNum];
  if (!cell) return { x: 0, y: 0 };
  const x = cell.col * CW;
  const y = cell.row * CH;
  // Top row (row 0): inner edge is bottom
  if (cell.row === 0) return { x: x + CW / 2, y: y + CH };
  // Bottom row (row 4): inner edge is top
  if (cell.row === 4) return { x: x + CW / 2, y: y };
  // Left col (col 0): inner edge is right
  if (cell.col === 0) return { x: x + CW, y: y + CH / 2 };
  // Right col (col 4): inner edge is left
  if (cell.col === 4) return { x: x, y: y + CH / 2 };
  return { x: x + CW / 2, y: y + CH / 2 };
};

// Format degree: 15.23 → "15°14'"
const formatDeg = (deg) => {
  const d = Math.floor(deg);
  const m = Math.round((deg - d) * 60);
  return `${d}°${m < 10 ? '0' : ''}${m}'`;
};

// Format declination: 12.34 → "+12.3°"
const formatDecl = (decl) => {
  const sign = decl >= 0 ? '+' : '';
  return `${sign}${decl.toFixed(1)}°`;
};

const NatalChartSVG = ({
  chartData, drawAspectLines, aspectMode, angularLines, children, flashPlanets,
}) => {
  /**
   * Which set of aspect lines to draw: 'none', 'vedic' or 'angular'.
   *
   * <p>`drawAspectLines` is the older boolean and is still honoured, because this component has a
   * second caller that passes it and has no opinion about the new modes. Deriving one from the
   * other here rather than changing both call sites keeps that caller working untouched — and the
   * boolean maps onto exactly two of the three modes, so nothing is lost in the translation.
   */
  const mode = aspectMode || (drawAspectLines ? 'vedic' : 'none');
  const houses = chartData?.houses || [];
  const planets = chartData?.planets || [];
  const ascendantLon = chartData?.ascendantLon;
  const midheavenLon = chartData?.mcLon;

  // One definition, shared with the aspect finder: a line drawn from the wrong cell points at the
  // wrong planet, and two copies of this arithmetic are two chances for it to.
  const mcHouse = mcHouseFor(chartData);

  // Group planets by house
  const planetsByHouse = {};
  for (let h = 1; h <= 12; h++) planetsByHouse[h] = [];
  planets.forEach(p => {
    if (p.house >= 1 && p.house <= 12) {
      planetsByHouse[p.house].push(p);
    }
  });

  // House sign lookup
  const houseSign = {};
  houses.forEach(h => { houseSign[h.house] = h; });

  // Build aspect lines
  // Vedic counting: house offset starts from planet's own house (offset 1 = same house)
  // So 7th house aspect → target = fromHouse + 6 (mod 12)
  const aspectLines = [];
  if (mode === 'angular' && Array.isArray(angularLines)) {
    // Handed in, not computed here. An angular aspect is a CROSSING between two frames, and this
    // component sees one frame at a time - it cannot tell a four-minute step from a jump to another
    // date, and the difference is the whole meaning of the line. See utils/angularAspects.js.
    aspectLines.push(...angularLines);
  }
  if (mode === 'vedic' && planets.length > 0) {
    planets.forEach(p => {
      const fromHouse = p.house;
      const offsets = [7];
      const special = VEDIC_SPECIAL_ASPECTS[p.abbr];
      if (special) offsets.push(...special);

      offsets.forEach(offset => {
        const toHouse = ((fromHouse - 1 + (offset - 1)) % 12) + 1;
        const is7th = offset === 7;

        // Skip Ra-Ke opposition — they are always in opposition
        if (is7th && (p.abbr === 'Ra' || p.abbr === 'Ke')) return;

        // Only draw when BOTH houses have at least one planet
        if ((planetsByHouse[fromHouse] || []).length === 0) return;
        if ((planetsByHouse[toHouse]   || []).length === 0) return;

        aspectLines.push({
          from: fromHouse,
          to: toHouse,
          color: is7th ? SEVENTH_ASPECT_COLOR : (SPECIAL_ASPECT_COLORS[p.abbr] || '#666'),
          dash: is7th ? SEVENTH_ASPECT_DASH : 'none',
          width: is7th ? 1.5 : (SPECIAL_ASPECT_WIDTH[p.abbr] || 1.5),
          abbr: p.abbr,
          offset,
        });
      });
    });
  }

  // Collect unique marker colors so each arrow color matches its line
  const markerColors = [...new Set(aspectLines.map(l => l.color))];

  const renderHouseCell = (houseNum) => {
    const cell = HOUSE_CELLS[houseNum];
    const x = cell.col * CW;
    const y = cell.row * CH;
    const isAsc = houseNum === 1;
    const sign = houseSign[houseNum];
    const planetsInHouse = planetsByHouse[houseNum] || [];

    // Extra label for House 1 (rising) and MC in its true house
    let specialLabel = null;
    if (houseNum === 1 && ascendantLon != null) {
      const ascNak = chartData?.ascendantNakshatraAbbr || '';
      const ascPada = chartData?.ascendantNakshatraPada || '';
      const nakPart = ascNak ? ` ${ascNak}/${ascPada}` : '';
      specialLabel = `Asc ${formatDeg(ascendantLon % 30)}${nakPart}`;
    } else if (mcHouse === houseNum && midheavenLon != null) {
      const mcNak = chartData?.mcNakshatraAbbr || '';
      const mcPada = chartData?.mcNakshatraPada || '';
      const nakPart = mcNak ? ` ${mcNak}/${mcPada}` : '';
      specialLabel = `MC ${formatDeg(midheavenLon % 30)}${nakPart}`;
    }

    return (
      <g key={`house-${houseNum}`}>
        <rect x={x} y={y} width={CW} height={CH}
          fill={isAsc ? '#b3e5fc' : '#f3e0ec'}
          stroke="#333" strokeWidth={1.5} />

        {/* Sign number — bottom-left corner */}
        {sign && (
          <text x={x + 6} y={y + CH - 6} fontSize={18} fontWeight="bold" fill="#555">
            {sign.signIndex}
          </text>
        )}

        {/* House label — top-right corner */}
        <text x={x + CW - 6} y={y + 18} fontSize={14} fill="#999" textAnchor="end">
          H{houseNum}
        </text>

        {/* Special label (Asc / MC) — top-left */}
        {specialLabel && (
          <text x={x + 6} y={y + 16} fontSize={13} fontWeight="bold" fill="#1565c0">
            {specialLabel}
          </text>
        )}

        {/* Planet info — bold, 16px */}
        {planetsInHouse.map((p, idx) => {
          const lineY = y + 36 + idx * 20;
          if (lineY + 14 > y + CH) return null;
          const retro = p.retrograde ? ' R' : '';
          const padaSuffix = p.nakshatraPada ? `/${p.nakshatraPada}` : '';
          const isFlashing = flashPlanets && flashPlanets.has(p.abbr);
          const label = `${p.abbr} ${formatDeg(p.degInSign)} ${p.nakshatraAbbr}${padaSuffix} ${formatDecl(p.declination)}${retro}`;
          return (
            <text key={p.name} x={x + CW / 2} y={lineY} fontSize={16}
              fontWeight="bold" textAnchor="middle"
              fill={isFlashing ? '#d50000' : '#222'}>
              {isFlashing && <>
                <animate attributeName="fill" values="#d50000;#222;#d50000" dur="0.6s" repeatCount="indefinite" />
              </>}
              {label}
            </text>
          );
        })}
      </g>
    );
  };

  return (
    <div style={{ position: 'relative', width: SVG_W, height: SVG_H }}>
      <svg width={SVG_W} height={SVG_H} style={{ position: 'absolute', top: 0, left: 0 }}>
        <defs>
          {markerColors.map(c => (
            <marker key={c} id={`arrow-${c.replace('#', '')}`} viewBox="0 0 10 10" refX="9" refY="5"
              markerWidth={6} markerHeight={6} orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
            </marker>
          ))}
        </defs>

        {/* Empty corner cells */}
        {[[0,0],[0,4],[4,0],[4,4]].map(([r,c]) => (
          <rect key={`empty-${r}-${c}`}
            x={c * CW} y={r * CH} width={CW} height={CH}
            fill="#fafafa" stroke="#ccc" strokeWidth={0.5} />
        ))}

        {/* Center 3×3 border */}
        <rect x={CW} y={CH} width={3 * CW} height={3 * CH}
          fill="white" stroke="#333" strokeWidth={2} />

        {/* House cells */}
        {Object.keys(HOUSE_CELLS).map(h => renderHouseCell(Number(h)))}

        {/* Aspect lines — from inner edge of source to inner edge of target */}
        {aspectLines.map((line, idx) => {
          const from = houseInnerEdgeMid(line.from);
          const to = houseInnerEdgeMid(line.to);
          const markerId = `arrow-${line.color.replace('#', '')}`;
          return (
            <line key={`aspect-${idx}`}
              x1={from.x} y1={from.y} x2={to.x} y2={to.y}
              stroke={line.color} strokeWidth={line.width} strokeOpacity={0.75}
              strokeDasharray={line.dash === 'none' ? undefined : line.dash}
              markerEnd={`url(#${markerId})`} />
          );
        })}
      </svg>

      {/* Center overlay for input controls (children) */}
      <div style={{
        position: 'absolute',
        left: CW, top: CH,
        width: 3 * CW, height: 3 * CH,
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        pointerEvents: 'auto',
        padding: 10,
        boxSizing: 'border-box',
      }}>
        {children}
      </div>
    </div>
  );
};

NatalChartSVG.propTypes = {
  chartData: PropTypes.shape({
    ascendantLon: PropTypes.number,
    ascendantSign: PropTypes.number,
    ascendantNakshatraAbbr: PropTypes.string,
    ascendantNakshatraPada: PropTypes.number,
    mcLon: PropTypes.number,
    mcNakshatraAbbr: PropTypes.string,
    mcNakshatraPada: PropTypes.number,
    planets: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string,
      siderealLon: PropTypes.number,
      signIndex: PropTypes.number,
      signName: PropTypes.string,
      degInSign: PropTypes.number,
      nakshatra: PropTypes.string,
      nakshatraAbbr: PropTypes.string,
      nakshatraPada: PropTypes.number,
      declination: PropTypes.number,
      retrograde: PropTypes.bool,
      house: PropTypes.number,
    })),
    houses: PropTypes.arrayOf(PropTypes.shape({
      house: PropTypes.number,
      signIndex: PropTypes.number,
      signName: PropTypes.string,
    })),
  }),
  /** Legacy boolean, still honoured: true means 'vedic'. */
  drawAspectLines: PropTypes.bool,
  /** 'none' | 'vedic' | 'angular'. Takes precedence over drawAspectLines when given. */
  aspectMode: PropTypes.oneOf(['none', 'vedic', 'angular']),
  /** Lines for aspects that became exact during the step just taken; see angularAspects.js. */
  angularLines: PropTypes.array,
  flashPlanets: PropTypes.instanceOf(Set),
  children: PropTypes.node,
};

NatalChartSVG.defaultProps = {
  chartData: null,
  drawAspectLines: false,
  flashPlanets: null,
};

export default NatalChartSVG;
